import React, { useEffect, useState } from 'react';
import { Box, Collapse, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

const ITEM_DEFAULT = '#c4ccd8';
const ITEM_ACTIVE = '#f97316';
const ITEM_ACTIVE_BG = 'rgba(249, 115, 22, 0.14)';

const SUB_ITEMS = [
  { path: '/stripe/overview', label: 'Overview', icon: <DashboardOutlinedIcon sx={{ fontSize: 18 }} /> },
  { path: '/stripe/transactions', label: 'Transactions', icon: <SwapHorizOutlinedIcon sx={{ fontSize: 18 }} /> },
  { path: '/stripe/customers', label: 'Customers', icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} /> },
  { path: '/stripe/subscriptions', label: 'Subscriptions', icon: <SubscriptionsOutlinedIcon sx={{ fontSize: 18 }} /> },
  { path: '/stripe/payouts', label: 'Payouts', icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} /> },
];

type Props = {
  pathname: string;
  navigate: (path: string) => void;
  onNavigate: () => void;
};

export function StripeNavGroup({ pathname, navigate, onNavigate }: Props) {
  const [open, setOpen] = useState(() => pathname.startsWith('/stripe'));
  const parentSelected = pathname.startsWith('/stripe');

  useEffect(() => {
    if (pathname.startsWith('/stripe')) {
      setOpen(true);
    }
  }, [pathname]);

  const subStyles = (path: string) => {
    const selected =
      pathname === path ||
      (path === '/stripe/overview' && (pathname === '/stripe' || pathname === '/stripe/overview'));
    return {
      pl: 3,
      py: 0.75,
      borderRadius: 1.5,
      mb: 0.25,
      borderLeft: selected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
      bgcolor: selected ? ITEM_ACTIVE_BG : 'transparent',
      color: selected ? ITEM_ACTIVE : ITEM_DEFAULT,
      '&:hover': {
        bgcolor: selected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
      },
    };
  };

  return (
    <Box>
      <ListItemButton
        onClick={() => {
          if (!open) {
            navigate('/stripe/overview');
            onNavigate();
          }
          setOpen((v) => !v);
        }}
        sx={{
          borderRadius: 1.5,
          py: 1,
          mb: 0.25,
          pl: 2,
          borderLeft: parentSelected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
          bgcolor: parentSelected ? ITEM_ACTIVE_BG : 'transparent',
          color: parentSelected ? ITEM_ACTIVE : ITEM_DEFAULT,
          '&:hover': {
            bgcolor: parentSelected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
          },
        }}
      >
        <ListItemIcon sx={{ color: parentSelected ? ITEM_ACTIVE : 'inherit', minWidth: 40 }}>
          <CreditCardOutlinedIcon sx={{ fontSize: 22, color: '#a78bfa' }} />
        </ListItemIcon>
        <ListItemText
          primary="Stripe"
          primaryTypographyProps={{ fontWeight: parentSelected ? 700 : 500, fontSize: '0.9rem' }}
        />
        {open ? <ExpandLess sx={{ color: ITEM_DEFAULT }} /> : <ExpandMore sx={{ color: ITEM_DEFAULT }} />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 1, pr: 0.5, pb: 0.5 }}>
          {SUB_ITEMS.map((item) => (
            <ListItemButton
              key={item.path}
              onClick={() => {
                navigate(item.path);
                onNavigate();
              }}
              sx={subStyles(item.path)}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.82rem' }} />
            </ListItemButton>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
